/**
 * La Oficina en corte: cada piso es un cliente y cada habitación un proyecto.
 * Abajo están las áreas comunes, donde pasean quienes no revisan un reporte.
 *
 * Módulo aparte. El home solo lo monta; para quitarlo, borra esta carpeta y
 * la línea de MainLayout. No escribe campos de reportes, usuarios ni roles.
 *
 * Para revisar el arte sin esperar: en la consola del navegador,
 * `window.__cmOficina = { hora: 22, evento: 'disco' }` fuerza la hora del día
 * y un evento (ids en officeEvents.js). `window.__cmOficina = null` lo quita.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { useData } from '../DataContext';
import { useNavigation } from '../NavigationContext';
import { drawOffice } from './drawOffice';
import { officeEventAt, officeEventById } from './officeEvents';
import { STAGE_H, boundsFor, buildOffice, cameraPanToFit } from './officeMap';
import { createSim, stepSim } from './officeSim';

const ZOOM_HOME = 1;
const ZOOM_MIN = 0.28;
const ZOOM_MAX = 2.2;
const AREAS = ['Data', 'Medios', 'Social', 'Cuentas', 'Sistemas', 'Dirección', 'Legal', 'RH', 'Diseño', 'Otra'];

const AvatarWorld = ({ showMap = false, activeReport = null }) => {
    const { user, userData, userRole, userPermissions, fetchWithAuth, API_URL } = useAuth();
    const { dashboards, clients, projects } = useData();
    const { openReport } = useNavigation();
    /* La oficina nace oculta en cada carga: solo se ve el botón para mostrarla. */
    const [hidden, setHidden] = useState(true);
    const [people, setPeople] = useState([]);
    const [panel, setPanel] = useState(null);
    const [note, setNote] = useState('');
    const [area, setArea] = useState(AREAS[0]);
    const [reportId, setReportId] = useState('');
    const [accessType, setAccessType] = useState('view');
    const [notice, setNotice] = useState('');
    const [followId, setFollowId] = useState('');
    const [zoom, setZoom] = useState(ZOOM_HOME);
    const [section, setSection] = useState('all');
    const [places, setPlaces] = useState({});

    const canvasRef = useRef(null);
    const stageRef = useRef(null);
    const simRef = useRef(null);
    const placesRef = useRef({ key: '', at: 0 });
    const hitsRef = useRef([]);
    const rosterRef = useRef([]);
    const followRef = useRef('');
    const cameraRef = useRef({ panX: 40, panY: 20, fit: true, fitZoom: true, dpr: 2, stageW: 900, stageH: STAGE_H });
    const dragRef = useRef({ on: false, moved: false, x: 0, y: 0, panX: 0, panY: 0 });

    const map = useMemo(
        () => buildOffice(dashboards, clients, projects),
        [dashboards, clients, projects, buildOffice]
    );
    const reportIds = useMemo(
        () => (dashboards || []).filter((item) => item && item.id && !item.isDraft).map((item) => item.id).slice(0, 80),
        [dashboards]
    );
    const reports = useMemo(
        () => (dashboards || []).filter((item) => item && item.id && !item.isDraft),
        [dashboards]
    );

    const self = useMemo(() => {
        if (!user) return null;
        const working = Boolean(activeReport && activeReport.id);
        return {
            uid: user.uid,
            nombre: userData?.nombre || 'Tú',
            photoURL: userData?.photoURL || '',
            rol: userRole || '',
            place: working ? 'station' : 'lobby',
            clientId: working ? (activeReport.clientId || '') : '',
            projectId: working ? (activeReport.projectId || '') : '',
            reportId: working ? activeReport.id : '',
            reportName: working ? (activeReport.reporteNombre || '') : '',
            action: working ? 'viewing' : 'idle',
        };
    }, [user, userData, userRole, activeReport]);

    const roster = useMemo(() => {
        if (!self) return people;
        return [self, ...people.filter((person) => person.uid !== self.uid)];
    }, [people, self]);
    rosterRef.current = roster;
    followRef.current = followId;

    useEffect(() => {
        if (!user) return undefined;
        const idsKey = reportIds.join(',');
        let stop = false;

        const publish = async () => {
            try {
                await fetchWithAuth(`${API_URL}/api/avatar-world/presence`, {
                    method: 'PUT',
                    silent: true,
                    body: JSON.stringify({
                        place: self?.place || 'lobby',
                        clientId: self?.clientId || '',
                        projectId: self?.projectId || '',
                        reportId: self?.reportId || '',
                        reportName: self?.reportName || '',
                        reportIds: idsKey ? idsKey.split(',') : [],
                    }),
                });
            } catch (error) {
                console.error('Presencia de la oficina:', error.message || error);
            }
        };
        const pull = async () => {
            try {
                const res = await fetchWithAuth(`${API_URL}/api/avatar-world/presence`, { silent: true });
                const data = await res.json();
                if (!stop) setPeople(Array.isArray(data.people) ? data.people : []);
            } catch (error) {
                console.error('Lectura de la oficina:', error.message || error);
            }
        };

        publish();
        pull();
        const pushTimer = setInterval(publish, 20000);
        const pullTimer = setInterval(pull, 15000);
        return () => {
            stop = true;
            clearInterval(pushTimer);
            clearInterval(pullTimer);
        };
    }, [user, API_URL, fetchWithAuth, reportIds, self]);

    useEffect(() => {
        if (!user) return undefined;
        const leave = () => {
            user.getIdToken().then((token) => {
                fetch(`${API_URL}/api/avatar-world/presence`, {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` },
                    keepalive: true,
                }).catch(() => {});
            }).catch(() => {});
        };
        window.addEventListener('pagehide', leave);
        return () => {
            window.removeEventListener('pagehide', leave);
            leave();
        };
    }, [user, API_URL]);

    useEffect(() => {
        simRef.current = null;
        cameraRef.current.fit = true;
        cameraRef.current.fitZoom = true;
    }, [map.width, map.height]);

    useLayoutEffect(() => {
        if (!showMap || hidden || !user) return undefined;
        const canvas = canvasRef.current;
        const stage = stageRef.current;
        if (!canvas || !stage) return undefined;
        const ctx = canvas.getContext('2d');
        if (!ctx) return undefined;
        let frameId = 0;
        const loop = (now) => {
            const stageW = Math.max(320, stage.clientWidth || 900);
            const stageH = STAGE_H;
            const dpr = Math.min(3, window.devicePixelRatio || 2);
            if (canvas.width !== Math.floor(stageW * dpr) || canvas.height !== Math.floor(stageH * dpr)) {
                canvas.width = Math.floor(stageW * dpr);
                canvas.height = Math.floor(stageH * dpr);
            }
            if (cameraRef.current.fit) {
                const bounds = boundsFor(map, section);
                let z = zoom;
                if (cameraRef.current.fitZoom) {
                    const spanW = Math.max(1, bounds.maxX - bounds.minX);
                    const spanH = Math.max(1, bounds.maxY - bounds.minY);
                    z = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.min((stageW - 12) / spanW, (stageH - 12) / spanH) * 0.96));
                    z = Number(z.toFixed(2));
                    if (Math.abs(z - zoom) > 0.02) setZoom(z);
                    cameraRef.current.fitZoom = false;
                }
                const pan = cameraPanToFit(bounds, stageW, stageH, z);
                cameraRef.current.panX = pan.panX;
                cameraRef.current.panY = pan.panY;
                cameraRef.current.fit = false;
            }
            cameraRef.current.dpr = dpr;
            cameraRef.current.stageW = stageW;
            cameraRef.current.stageH = stageH;
            if (!simRef.current) simRef.current = createSim();
            const wall = Date.now();
            const forced = window.__cmOficina || null;
            const liveEvent = forced && forced.evento ? officeEventById(forced.evento, wall) : officeEventAt(wall);
            const frame = stepSim(simRef.current, map, rosterRef.current, now, wall, liveEvent);
            const actors = frame.actors;
            const camera = {
                zoom,
                panX: cameraRef.current.panX,
                panY: cameraRef.current.panY,
                section,
                stageW,
                stageH,
                dpr,
            };
            try {
                hitsRef.current = drawOffice(ctx, map, frame, wall, user.uid, liveEvent, camera, forced && forced.hora != null ? { hour: Number(forced.hora) } : null);
            } catch (error) {
                console.error('Dibujo de la oficina:', error);
                return;
            }
            if (now - placesRef.current.at > 1500) {
                const next = {};
                actors.forEach((actor) => {
                    next[actor.person.uid] = actor.label;
                });
                const key = JSON.stringify(next);
                placesRef.current.at = now;
                if (key !== placesRef.current.key) {
                    placesRef.current.key = key;
                    setPlaces(next);
                }
            }
            const followed = followRef.current && actors.find((actor) => actor.person.uid === followRef.current);
            if (followed && followed.sx != null) {
                cameraRef.current.panX = stageW / 2 - followed.sx * zoom;
                cameraRef.current.panY = stageH / 2 - followed.sy * zoom;
            }
            frameId = requestAnimationFrame(loop);
        };
        loop(performance.now());
        const onWheelNative = (event) => {
            event.preventDefault();
            const delta = event.deltaY > 0 ? -0.15 : 0.15;
            const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom + delta));
            setZoom(Number(next.toFixed(2)));
            cameraRef.current.fit = true;
            cameraRef.current.fitZoom = false;
        };
        canvas.addEventListener('wheel', onWheelNative, { passive: false });
        return () => {
            cancelAnimationFrame(frameId);
            canvas.removeEventListener('wheel', onWheelNative);
        };
    }, [showMap, hidden, map, user, zoom, section]);

    const toggleHidden = () => setHidden((prev) => !prev);

    const worldFromEvent = (event) => {
        const canvas = canvasRef.current;
        const cam = cameraRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const px = ((event.clientX - rect.left) * canvas.width) / rect.width;
        const py = ((event.clientY - rect.top) * canvas.height) / rect.height;
        const dpr = cam.dpr || 1;
        return {
            x: (px - cam.panX * dpr) / (dpr * zoom),
            y: (py - cam.panY * dpr) / (dpr * zoom),
        };
    };

    const onPointerDown = (event) => {
        dragRef.current = {
            on: true,
            moved: false,
            x: event.clientX,
            y: event.clientY,
            panX: cameraRef.current.panX,
            panY: cameraRef.current.panY,
        };
    };

    const onPointerMove = (event) => {
        if (!dragRef.current.on) return;
        const dx = event.clientX - dragRef.current.x;
        const dy = event.clientY - dragRef.current.y;
        if (Math.hypot(dx, dy) > 4) dragRef.current.moved = true;
        cameraRef.current.panX = dragRef.current.panX + dx;
        cameraRef.current.panY = dragRef.current.panY + dy;
        setFollowId('');
    };

    const onPointerUp = (event) => {
        const dragged = dragRef.current.moved;
        dragRef.current.on = false;
        if (dragged) return;
        const world = worldFromEvent(event);
        if (!world) return;
        const personHit = hitsRef.current.find((hit) => hit.kind === 'person' && Math.hypot(hit.sx - world.x, hit.sy - world.y) < 48);
        if (personHit) {
            setNotice('');
            setPanel({ person: personHit.person, mode: 'card' });
            return;
        }
        const deskHit = hitsRef.current.find((hit) => hit.kind === 'desk' && world.x >= hit.x && world.x <= hit.x + hit.w && world.y >= hit.y && world.y <= hit.y + hit.h);
        if (deskHit) {
            const report = reports.find((item) => item.projectId === deskHit.projectId);
            if (report) openReport(report);
        }
    };

    const personReport = panel
        ? reports.find((item) => item.id === panel.person.reportId)
        : null;
    const canAssign = userPermissions?.permissions_assign_direct === true;
    const canResponsible = userPermissions?.reports_manage_responsibles === true;
    const isSelf = panel && user && panel.person.uid === user.uid;

    const sendNote = async () => {
        if (!panel) return;
        setNotice('');
        try {
            await fetchWithAuth(`${API_URL}/api/avatar-world/note`, {
                method: 'POST',
                body: JSON.stringify({ userId: panel.person.uid, text: note }),
            });
            setNote('');
            setNotice('Nota enviada. Le llega a la campana.');
            setPanel({ ...panel, mode: 'card' });
        } catch (error) {
            setNotice(error.message || 'No se pudo enviar la nota.');
        }
    };

    const makeResponsible = async () => {
        if (!panel || !reportId) return;
        setNotice('');
        try {
            await fetchWithAuth(`${API_URL}/admin/reportes-maestros/${reportId}/responsibles`, {
                method: 'POST',
                body: JSON.stringify({ userId: panel.person.uid, area }),
            });
            setNotice('Quedó como responsable.');
            setPanel({ ...panel, mode: 'card' });
        } catch (error) {
            setNotice(error.message || 'No se pudo asignar como responsable.');
        }
    };

    const giveAccess = async () => {
        if (!panel || !reportId) return;
        const report = reports.find((item) => item.id === reportId);
        if (!report) return;
        setNotice('');
        try {
            await fetchWithAuth(`${API_URL}/admin/assign-report`, {
                method: 'POST',
                body: JSON.stringify({
                    userId: panel.person.uid,
                    reporteId: report.id,
                    dashboardUrl: report.dashboardUrl || '',
                    accessType,
                    cliente: report.cliente || '',
                    clientId: report.clientId || '',
                    proyecto: report.proyecto || '',
                    projectId: report.projectId || '',
                    reporteNombre: report.reporteNombre || '',
                }),
            });
            setNotice('Acceso asignado.');
            setPanel({ ...panel, mode: 'card' });
        } catch (error) {
            setNotice(error.message || 'No se pudo asignar el acceso.');
        }
    };

    if (!showMap) return null;

    if (hidden) {
        return (
            <button type="button" onClick={toggleHidden} style={ui.showBtn}>
                Mostrar la oficina
            </button>
        );
    }

    const placeOf = (person) => places[person.uid] || (person.place === 'station' ? (person.reportName || 'reporte') : 'Lobby');
    const where = panel && panel.person.place === 'station'
        ? `Trabajando en ${panel.person.reportName || 'un reporte'}`
        : `En ${panel ? placeOf(panel.person).toLowerCase() : 'el lobby'}`;

    return (
        <section style={ui.frame} aria-label="La Oficina">
            <header style={ui.bar}>
                <div>
                    <div style={ui.kicker}>Corte del edificio</div>
                    <h2 style={ui.title}>La Oficina</h2>
                </div>
                <div style={ui.barActions}>
                    <span style={ui.count}>{roster.length} en línea</span>
                    {followId ? (
                        <button type="button" onClick={() => setFollowId('')} style={ui.ghost}>Dejar de seguir</button>
                    ) : null}
                    <button type="button" onClick={toggleHidden} style={ui.ghost}>Ocultar</button>
                </div>
            </header>
            <div style={ui.toolbar}>
                <button type="button" style={ui.ghost} onClick={() => { setZoom((prev) => Math.min(ZOOM_MAX, Number((prev + 0.12).toFixed(2)))); cameraRef.current.fit = true; cameraRef.current.fitZoom = false; }}>+</button>
                <button type="button" style={ui.ghost} onClick={() => { setZoom((prev) => Math.max(ZOOM_MIN, Number((prev - 0.12).toFixed(2)))); cameraRef.current.fit = true; cameraRef.current.fitZoom = false; }}>−</button>
                <button type="button" style={ui.ghost} onClick={() => { setSection('all'); cameraRef.current.fit = true; cameraRef.current.fitZoom = true; }}>Encajar</button>
                <label style={ui.sectionLabel}>
                    Piso
                    <select value={section} onChange={(event) => { setSection(event.target.value); cameraRef.current.fit = true; cameraRef.current.fitZoom = true; }} style={ui.input}>
                        {(map.sections || []).map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                        ))}
                    </select>
                </label>
            </div>
            <div style={ui.stage}>
                <div ref={stageRef} style={ui.viewport}>
                    <canvas
                        ref={canvasRef}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerLeave={() => { dragRef.current.on = false; }}
                        style={ui.canvas}
                        aria-label="Oficina pixelada. Arrastra para mover, rueda para zoom, clic en una persona o en una oficina."
                    />
                </div>
                <aside style={ui.roster} aria-label="Usuarios en línea">
                    <div style={ui.rosterTitle}>En línea</div>
                    {roster.map((person) => (
                        <button
                            key={person.uid}
                            type="button"
                            style={ui.rosterRow}
                            onClick={() => {
                                setFollowId(person.uid);
                                setPanel({ person, mode: 'card' });
                            }}
                        >
                            <span style={ui.onlineDot} />
                            <span>
                                <strong style={ui.rosterName}>{person.nombre}</strong>
                                <span style={ui.rosterMeta}>{person.rol || 'usuario'} · {placeOf(person)}</span>
                            </span>
                        </button>
                    ))}
                </aside>
                {panel && (
                    <aside style={ui.card} role="dialog" aria-label={panel.person.nombre}>
                        <div style={ui.cardHead}>
                            {panel.person.photoURL ? (
                                <img src={panel.person.photoURL} alt="" style={ui.photo} />
                            ) : (
                                <span style={ui.photoFallback}>{(panel.person.nombre || '?').slice(0, 1)}</span>
                            )}
                            <div>
                                <strong style={ui.cardName}>{panel.person.nombre}</strong>
                                <div style={ui.meta}>{panel.person.rol || 'usuario'} · {where}</div>
                            </div>
                            <button type="button" onClick={() => setPanel(null)} style={ui.ghost}>Cerrar</button>
                        </div>

                        {panel.mode === 'card' && (
                            <div style={ui.actions}>
                                {!isSelf && (
                                    <button type="button" style={ui.action} onClick={() => { setNotice(''); setPanel({ ...panel, mode: 'note' }); }}>
                                        Dejar nota
                                    </button>
                                )}
                                <button type="button" style={ui.action} onClick={() => setFollowId(panel.person.uid)}>
                                    Seguir
                                </button>
                                {personReport && (
                                    <button type="button" style={ui.action} onClick={() => openReport(personReport)}>
                                        Abrir su reporte
                                    </button>
                                )}
                                {canResponsible && !isSelf && (
                                    <button type="button" style={ui.action} onClick={() => { setNotice(''); setReportId(reports[0]?.id || ''); setPanel({ ...panel, mode: 'responsible' }); }}>
                                        Hacer responsable
                                    </button>
                                )}
                                {canAssign && !isSelf && (
                                    <button type="button" style={ui.action} onClick={() => { setNotice(''); setReportId(reports[0]?.id || ''); setPanel({ ...panel, mode: 'access' }); }}>
                                        Dar acceso
                                    </button>
                                )}
                            </div>
                        )}

                        {panel.mode === 'note' && (
                            <div style={ui.form}>
                                <textarea
                                    value={note}
                                    onChange={(event) => setNote(event.target.value)}
                                    maxLength={240}
                                    placeholder="Escribe una nota corta"
                                    style={ui.area}
                                />
                                <div style={ui.formRow}>
                                    <button type="button" style={ui.action} onClick={() => setPanel({ ...panel, mode: 'card' })}>Volver</button>
                                    <button type="button" style={ui.primary} onClick={sendNote} disabled={!note.trim()}>Enviar</button>
                                </div>
                            </div>
                        )}

                        {panel.mode === 'responsible' && (
                            <div style={ui.form}>
                                <select value={reportId} onChange={(event) => setReportId(event.target.value)} style={ui.input}>
                                    {reports.map((report) => (
                                        <option key={report.id} value={report.id}>{report.reporteNombre}</option>
                                    ))}
                                </select>
                                <select value={area} onChange={(event) => setArea(event.target.value)} style={ui.input}>
                                    {AREAS.map((item) => <option key={item} value={item}>{item}</option>)}
                                </select>
                                <div style={ui.formRow}>
                                    <button type="button" style={ui.action} onClick={() => setPanel({ ...panel, mode: 'card' })}>Volver</button>
                                    <button type="button" style={ui.primary} onClick={makeResponsible} disabled={!reportId}>Asignar</button>
                                </div>
                            </div>
                        )}

                        {panel.mode === 'access' && (
                            <div style={ui.form}>
                                <select value={reportId} onChange={(event) => setReportId(event.target.value)} style={ui.input}>
                                    {reports.map((report) => (
                                        <option key={report.id} value={report.id}>{report.reporteNombre}</option>
                                    ))}
                                </select>
                                <select value={accessType} onChange={(event) => setAccessType(event.target.value)} style={ui.input}>
                                    <option value="view">Vista</option>
                                    <option value="edit">Edición</option>
                                </select>
                                <div style={ui.formRow}>
                                    <button type="button" style={ui.action} onClick={() => setPanel({ ...panel, mode: 'card' })}>Volver</button>
                                    <button type="button" style={ui.primary} onClick={giveAccess} disabled={!reportId}>Dar acceso</button>
                                </div>
                            </div>
                        )}
                        {notice ? <p style={ui.notice}>{notice}</p> : null}
                    </aside>
                )}
            </div>
            {notice && !panel ? <p style={ui.notice}>{notice}</p> : null}
        </section>
    );
};

const ui = {
    frame: {
        margin: '12px 16px 0',
        border: '4px solid #3a2418',
        backgroundColor: '#e8d5b5',
        boxShadow: '6px 6px 0 #3a2418',
        fontFamily: '"Courier New", monospace',
        color: '#241c33',
    },
    bar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        padding: '8px 12px',
        borderBottom: '4px solid #3a2418',
        backgroundColor: '#f3ddc4',
    },
    kicker: { fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase' },
    title: { margin: 0, fontSize: 18, letterSpacing: '0.04em' },
    barActions: { display: 'flex', alignItems: 'center', gap: 8 },
    count: { fontSize: 12 },
    ghost: {
        border: '3px solid #241c33',
        backgroundColor: '#f7f3ea',
        fontFamily: 'inherit',
        fontSize: 12,
        padding: '4px 8px',
        cursor: 'pointer',
    },
    showBtn: {
        margin: '12px 16px 0',
        border: '4px solid #3a2418',
        backgroundColor: '#e8d5b5',
        boxShadow: '4px 4px 0 #3a2418',
        fontFamily: '"Courier New", monospace',
        fontSize: 13,
        padding: '8px 12px',
        cursor: 'pointer',
    },
    stage: { position: 'relative', display: 'flex', minHeight: STAGE_H },
    viewport: {
        flex: 1,
        height: STAGE_H,
        overflow: 'hidden',
        backgroundColor: '#7ebfe8',
        cursor: 'grab',
    },
    canvas: {
        width: '100%',
        height: STAGE_H,
        imageRendering: 'pixelated',
        display: 'block',
        touchAction: 'none',
    },
    toolbar: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderBottom: '3px solid #3a2418',
        backgroundColor: '#ead4b8',
    },
    sectionLabel: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 },
    roster: {
        width: 220,
        flexShrink: 0,
        borderLeft: '3px solid #3a2418',
        backgroundColor: '#f7f3ea',
        overflow: 'auto',
        maxHeight: STAGE_H,
        padding: 8,
    },
    rosterTitle: { fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 },
    rosterRow: {
        display: 'flex',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        padding: '6px 4px',
        fontFamily: 'inherit',
    },
    rosterName: { display: 'block', fontSize: 12 },
    rosterMeta: { display: 'block', fontSize: 10, color: '#5c4636' },
    onlineDot: {
        width: 8,
        height: 8,
        marginTop: 4,
        borderRadius: 0,
        backgroundColor: '#3dff8a',
        flexShrink: 0,
        boxShadow: '0 0 0 2px #1a140e',
    },
    card: {
        position: 'absolute',
        top: 12,
        right: 236,
        width: 'min(320px, calc(100% - 24px))',
        backgroundColor: '#f7f3ea',
        border: '4px solid #241c33',
        boxShadow: '4px 4px 0 #241c33',
        padding: 10,
        zIndex: 2,
    },
    cardHead: { display: 'flex', gap: 8, alignItems: 'flex-start' },
    photo: { width: 36, height: 36, objectFit: 'cover', border: '3px solid #241c33', imageRendering: 'pixelated' },
    photoFallback: {
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#5e35b1',
        color: '#fff',
        border: '3px solid #241c33',
        fontWeight: 700,
    },
    cardName: { display: 'block', fontSize: 14 },
    meta: { fontSize: 11, lineHeight: 1.35, marginTop: 2 },
    actions: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
    action: {
        border: '3px solid #241c33',
        backgroundColor: '#fff',
        fontFamily: 'inherit',
        fontSize: 12,
        padding: '4px 8px',
        cursor: 'pointer',
    },
    primary: {
        border: '3px solid #241c33',
        backgroundColor: '#5e35b1',
        color: '#fff',
        fontFamily: 'inherit',
        fontSize: 12,
        padding: '4px 8px',
        cursor: 'pointer',
    },
    form: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 },
    formRow: { display: 'flex', justifyContent: 'flex-end', gap: 6 },
    input: {
        border: '3px solid #241c33',
        backgroundColor: '#fff',
        fontFamily: 'inherit',
        fontSize: 12,
        padding: '4px 6px',
    },
    area: {
        border: '3px solid #241c33',
        backgroundColor: '#fff',
        fontFamily: 'inherit',
        fontSize: 12,
        minHeight: 72,
        resize: 'vertical',
        padding: 6,
    },
    notice: { margin: '8px 12px', fontSize: 12 },
};

export default AvatarWorld;
